import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { ArrowBack, Email } from '@mui/icons-material';
import axios from 'axios';

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

interface ForgotPasswordFormData {
  email: string;
}

const authFieldSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'var(--auth-field-background) !important',
  },
  '& .MuiInputBase-root:hover': {
    backgroundColor: 'var(--auth-field-background) !important',
  },
  '& .MuiInputBase-root.Mui-focused': {
    backgroundColor: 'var(--auth-field-background) !important',
  },
  '& .MuiInputBase-input': {
    backgroundColor: 'transparent !important',
  },
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
    WebkitBoxShadow: '0 0 0 1000px var(--auth-field-background) inset !important',
    boxShadow: '0 0 0 1000px var(--auth-field-background) inset !important',
    WebkitTextFillColor: 'var(--text-primary) !important',
    transition: 'background-color 5000s ease-in-out 0s',
  }
};

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBackToLogin }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await axios.post('/api/auth/forgot-password', data);
      setMessage('Password reset link has been sent to your email address.');
      reset();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to send reset email. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glassContainer auth-form-container" style={{ padding: '20px', maxWidth: '500px', maxHeight: '90vh', background:'radial-gradient(circle at center, rgba(181, 231, 255, 0.2), rgba(0, 0, 0, 0.63) 70%)', position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', overflowY: 'auto' }}>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          onClick={onBackToLogin}
          startIcon={<ArrowBack />}
          variant="text"
          size="small"
          sx={{ mr: 2 }}
        >
          Back
        </Button>
      </Box>

      <div className="logobox glassBtn" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
        <img src="/logo192.png" alt="BROODbook Logo" className="logo" style={{ height: 40, marginRight: 8 }} />
        <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          fontFamily: 'inherit'
        }}>
          <span style={{ color: '#3a9397' }}>BROOD</span>
          <span style={{ color: '#ff9800' }}>book</span>
        </div>
      </div>

      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Email sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h5" gutterBottom>
          Forgot Password?
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Enter your email address and we'll send you a link to reset your password.
        </Typography>
      </Box>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address',
            },
          })}
          error={!!errors.email}
          helperText={errors.email?.message}
          sx={{ ...authFieldSx, mb: 3 }}
          disabled={loading}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ mb: 2 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
        </Button>
      </Box>

      <Typography align="center" variant="body2" color="textSecondary">
        Remember your password?{' '}
        <Button
          variant="text"
          onClick={onBackToLogin}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Sign in
        </Button>
      </Typography>
      
    </div>
  );
};